import React, { useState, useRef } from 'react';
import { Typography, Button, Box, TextField, Grid, Divider, CircularProgress } from '@mui/material';
import Header from "../../Layout/Header/header";
import Footer from "../../Layout/Footer/footer";
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import MessageIcon from '@mui/icons-material/Message';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { API_CONFIG } from "../../api/apiUrl";
import { motion } from 'framer-motion';

const contentVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 },
};

const contentTransition = {
  type: 'spring',
  stiffness: 120,
  damping: 20,
  duration: 0.3,
};

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    subject: '',
    description: '',
    createdDate: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const formRef = useRef(null);

  const validateField = (name: string, value: string) => {
    if (name === 'name') setNameError(value ? '' : 'Name is required');
    if (name === 'email') setEmailError(value ? (value.includes('@') ? '' : 'Please enter a valid email') : 'Email is required');
    if (name === 'mobile') setMobileError(value ? (value.replace(/\D/g, '').length >= 10 ? '' : 'Mobile number must be at least 10 digits') : '');
    if (name === 'description') setDescriptionError(value ? '' : 'Description is required');
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    let mobileValue = value;
    if (name === 'mobile') {
      const number = value.replace(/\D/g, '').slice(0, 10);
      mobileValue = number;
    }
    setFormData({ ...formData, [name]: mobileValue });
    validateField(name, mobileValue);
  };

  const isFormValid = () => {
    return (
      formData.name &&
      formData.email &&
      formData.email.includes('@') &&
      formData.description &&
      !nameError &&
      !emailError &&
      !mobileError &&
      !descriptionError
    );
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    validateField('name', formData.name);
    validateField('email', formData.email);
    validateField('mobile', formData.mobile);
    validateField('description', formData.description);

    if (!isFormValid()) return;

    setIsLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile ? parseInt(formData.mobile.replace(/\D/g, '')) : 0,
        subject: formData.subject,
        description: formData.description,
        createdDate: new Date().toISOString()
      };
      const response = await axios.post(API_CONFIG.CONTACT_US, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.status === 200 || response.status === 201) {
        setFormData({
          name: '',
          email: '',
          mobile: '',
          subject: '',
          description: '',
          createdDate: ''
        });
        setNameError('');
        setEmailError('');
        setMobileError('');
        setDescriptionError('');
        toast.success('Message sent successfully!');
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      toast.error('Error: ' + (error.response?.data?.detail || 'Failed to send message'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '2000px', margin: '0 auto' }}>
      <Toaster position="top-right" />
      <Header />
      <Box sx={{ flex: 1 }}>
        <motion.div
          variants={contentVariants}
          initial="initial"
          animate="in"
          exit="out"
          transition={contentTransition}
        >
          <Box sx={{
            py: { xs: 2, sm: 3, md: 3, lg: 3, xl: 3 },
            px: { xs: 1, sm: 0.8, md: 2, lg: 4, xl: 6 },
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)), url("/images/desi-pattern.png")',
            backgroundSize: 'cover'
          }}>
            <Box sx={styles.mainContainer}>
              <Box sx={styles.header}>
                <Typography sx={{
                  fontSize: { xs: '20px', sm: '25px', md: '25px' },
                  fontWeight: 500,
                  lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 }
                }}>
                  Contact Us
                </Typography>
                <Typography variant="body1" sx={{ color: 'white' }}>
                  We'd love to hear from you
                </Typography>
              </Box>

              <Grid container spacing={0}>
                <Grid item xs={12} md={7} lg={8}>
                  <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }} ref={formRef}>
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#89652A', display: 'flex', alignItems: 'center' }}>
                            <ContactSupportIcon sx={{ mr: 1 }} />
                            Send Us a Message
                          </Typography>
                          <Divider sx={{ mb: 3, borderColor: 'rgba(182, 157, 116, 0.3)' }} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography sx={styles.inputLabel}>Full Name*</Typography>
                          <TextField
                            fullWidth
                            name="name"
                            placeholder="Enter your name"
                            variant="outlined"
                            size="medium"
                            sx={{
                              ...styles.textField,
                              '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#B69D74',
                              },
                            }}
                            onChange={handleChange}
                            value={formData.name}
                            InputProps={{
                              startAdornment: <PersonIcon sx={{ mr: 1, color: '#89652A' }} />
                            }}
                            error={!!nameError}
                            helperText={nameError}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography sx={styles.inputLabel}>Email Address*</Typography>
                          <TextField
                            fullWidth
                            name="email"
                            placeholder="Enter your email"
                            variant="outlined"
                            size="medium"
                            sx={{
                              ...styles.textField,
                              '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#B69D74',
                              },
                            }}
                            onChange={handleChange}
                            value={formData.email}
                            InputProps={{
                              startAdornment: <EmailIcon sx={{ mr: 1, color: '#89652A' }} />
                            }}
                            error={!!emailError}
                            helperText={emailError}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography sx={styles.inputLabel}>Phone Number</Typography>
                          <TextField
                            fullWidth
                            name="mobile"
                            placeholder="Enter your phone number"
                            variant="outlined"
                            size="medium"
                            sx={{
                              ...styles.textField,
                              '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#B69D74',
                              },
                            }}
                            onChange={handleChange}
                            value={formData.mobile}
                            InputProps={{
                              startAdornment: <PhoneIcon sx={{ mr: 1, color: '#89652A' }} />
                            }}
                            error={!!mobileError}
                            helperText={mobileError}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography sx={styles.inputLabel}>Subject</Typography>
                          <TextField
                            fullWidth
                            name="subject"
                            placeholder="How can we help you?"
                            variant="outlined"
                            size="medium"
                            sx={styles.textField}
                            onChange={handleChange}
                            value={formData.subject}
                            InputProps={{
                              startAdornment: <MessageIcon sx={{ mr: 1, color: '#89652A' }} />
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography sx={styles.inputLabel}>Description*</Typography>
                          <TextField
                            fullWidth
                            name="description"
                            placeholder="Type your message here"
                            variant="outlined"
                            size="medium"
                            sx={{
                              ...styles.textField,
                              '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#B69D74',
                              },
                            }}
                            onChange={handleChange}
                            value={formData.description}
                            multiline
                            rows={5}
                            error={!!descriptionError}
                            helperText={descriptionError}
                          />
                        </Grid>

                        <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                          <Button
                            type="submit"
                            sx={styles.submitButton}
                            disabled={!isFormValid() || isLoading}
                            size="large"
                          >
                            {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send Message'}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </Box>
                </Grid>

                <Grid item xs={12} md={5} lg={4}>
                  <Box sx={styles.contactInfoContainer}>
                    <Typography variant="h5" fontWeight={600} color="white" mb={4}>
                      Get in Touch
                    </Typography>

                    {/* <Box sx={styles.contactItem}>
                      <Box sx={styles.contactIconBox}><LocationOnIcon /></Box>
                      <Box>
                        <Typography variant="h6" color="white" fontWeight={500}>Our Location</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.8)" mt={0.5}>123 Desi Lane, New Delhi, India 110001</Typography>
                      </Box>
                    </Box> */}

                    <Box sx={styles.contactItem}>
                      <Box sx={styles.contactIconBox}><EmailIcon /></Box>
                      <Box>
                        <Typography variant="h6" color="white" fontWeight={500}>Email Us</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.8)" mt={0.5}>info@meegloo.com</Typography>
                      </Box>
                    </Box>

                    <Box sx={styles.contactItem}>
                      <Box sx={styles.contactIconBox}><PhoneIcon /></Box>
                      <Box>
                        <Typography variant="h6" color="white" fontWeight={500}>Call Us</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.8)" mt={0.5}>+91 8130664669</Typography>
                      </Box>
                    </Box>

                    <Box sx={styles.contactItem}>
                      <Box sx={styles.contactIconBox}><WhatsAppIcon /></Box>
                      <Box>
                        <Typography variant="h6" color="white" fontWeight={500}>WhatsApp</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.8)" mt={0.5}>+91 8130664669</Typography>
                      </Box>
                    </Box>

                    <Box sx={styles.businessHours}>
                      <Typography variant="h6" color="white" fontWeight={500} mb={1}>Business Hours</Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.8)">Monday - Friday: 9:00 AM - 6:00 PM</Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.8)">Saturday: 10:00 AM - 4:00 PM</Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.8)">Sunday: Closed</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </motion.div>
      </Box>

      <Footer />
    </Box>
  );
};

const styles: any = {
  mainContainer: { borderRadius: { xs: "10px", sm: "10px" }, boxShadow: '0px 0px 3px 0px #00000033', overflow: 'hidden', background: '#fff', mb: 4 },
  header: { background: "linear-gradient(90deg, #89652A 0%, #B69D74 100%)", color: "#fff", padding: { xs: "10px 12px", sm: "14px 20px", md: "16px 24px" }, borderTopLeftRadius: { xs: "6px", sm: "8px" }, borderTopRightRadius: { xs: "6px", sm: "8px" }, position: 'relative', '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0, height: '6px', background: 'url("/images/desi-border.png") repeat-x', backgroundSize: 'auto 100%' } },
  textField: { 
    '& .MuiOutlinedInput-root': { 
      borderRadius: '12px', 
      borderColor: '#89652A', 
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#B69D74', borderWidth: '1.5px' }, 
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' }, 
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A', borderWidth: '2px' },
   
    }, 
    '& .MuiInputBase-input': { padding: '14px 14px' } 
  },
  inputLabel: { fontSize: '16px', fontWeight: 500, mb: 1.5, color: '#333', fontFamily: 'Poppins, sans-serif' },
  submitButton: { 
    textTransform: 'capitalize', 
    fontSize: { xs: '16px', md: '16px' }, 
    fontWeight: 600, 
    borderRadius: '28px', 
    color: 'white', 
    bgcolor: '#89652A', 
    py: 0.7, 
    px: { xs: 4, md: 6 }, 
    '&:hover': { bgcolor: '#6b4e20' }, 
    '&:disabled': { bgcolor: '#a88b5e' } 
  },
  contactInfoContainer: { background: 'linear-gradient(135deg, #89652A 0%, #B69D74 100%)', height: '100%', padding: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'url("/images/paisley-pattern.png")', backgroundSize: '200px', opacity: 0.05, mixBlendMode: 'overlay' } },
  contactItem: { display: 'flex', mb: 3, alignItems: 'flex-start', position: 'relative', zIndex: 1 },
  contactIconBox: { width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, color: 'white', border: '1px solid rgba(255,255,255,0.2)' },
  businessHours: { mt: 4, p: 2, background: 'rgba(0,0,0,0.1)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', position: 'relative', zIndex: 1 },
};

export default ContactUs;